/**
 * Wishlist Drawer — localStorage wishlist with PCH line items
 */
(function () {
  'use strict';

  if (window.__wishlistDrawerInitialized) return;
  window.__wishlistDrawerInitialized = true;

  const STORAGE_KEY = 'untamed_wishlist';
  const SECTION_ID = 'wishlist-drawer-line-item';

  let config = { moneyFormat: '', currencyCode: '' };
  let wrapper;
  let overlay;
  let closeBtn;
  let countEl;
  let emptyEl;
  let itemsEl;
  let footerEl;
  let clearBtn;
  let addAllBtn;

  let isOpen = false;
  let items = [];
  let menuWasOpen = false;
  let renderInProgress = false;
  let pendingRender = false;

  function getConfig() {
    const el = document.querySelector('[data-wishlist-drawer-config]');
    if (!el) return;
    try {
      config = { ...config, ...JSON.parse(el.textContent) };
    } catch (e) {
      /* ignore */
    }
  }

  function loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      items = stored ? JSON.parse(stored) : [];

      let needsSave = false;
      items = items.filter((item) => {
        if (item.handle && (item.handle.startsWith('"') || item.handle.includes('&quot;'))) {
          needsSave = true;
          return false;
        }
        if (typeof item.id === 'string' && item.id.startsWith('"')) {
          needsSave = true;
          return false;
        }
        return true;
      });

      if (needsSave) saveToStorage();
    } catch (e) {
      console.error('Failed to load wishlist from storage:', e);
      items = [];
    }
  }

  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save wishlist to storage:', e);
    }
  }

  function normalizeCents(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.round(value);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return 0;
      if (/^-?\d+$/.test(trimmed)) return parseInt(trimmed, 10);
      const normalized = Number(trimmed.replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(normalized)) return Math.round(normalized * 100);
    }
    return 0;
  }

  function formatMoney(cents) {
    const normalizedCents = normalizeCents(cents);
    if (window.formatMoney) {
      return window.formatMoney(normalizedCents, config.moneyFormat || window.themeMoneyFormat);
    }
    if (window.formatMoneyFallback) {
      return window.formatMoneyFallback(normalizedCents, config.currencyCode || window.themeCurrencyCode);
    }
    return (normalizedCents / 100).toFixed(2);
  }

  function parseSectionHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const line = doc.querySelector('[data-wishlist-line-item]');
    if (line) return line.outerHTML;
    const section = doc.querySelector('.shopify-section');
    return section ? section.innerHTML : html;
  }

  function renderFallbackCard(item, index) {
    const priceHtml =
      item.compareAtPrice && item.compareAtPrice > item.price
        ? `<span class="product-card-horizontal__price product-card-horizontal__price--sale">${formatMoney(item.price)}</span>
           <span class="product-card-horizontal__compare-price">${formatMoney(item.compareAtPrice)}</span>`
        : `<span class="product-card-horizontal__price">${formatMoney(item.price)}</span>`;

    return `
      <article class="product-card-horizontal wishlist-drawer__line-item wishlist-drawer__line-item--fallback" data-wishlist-line-item data-product-id="${item.id}" style="--item-index: ${index}">
        <a href="${item.url}" class="product-card-horizontal__media-link">
          ${
            item.image
              ? `<span class="product-card-horizontal__media"><img src="${item.image}" alt="" class="product-card-horizontal__image" width="120" height="120" loading="lazy"></span>`
              : `<span class="product-card-horizontal__media"><span class="product-card-horizontal__placeholder"><span class="ph-bold ph-package"></span></span></span>`
          }
        </a>
        <div class="product-card-horizontal__content">
          <div class="product-card-horizontal__top">
            <h3 class="product-card-horizontal__title">${item.title}</h3>
            <button type="button" class="wishlist-drawer__line-remove" data-wishlist-remove data-product-id="${item.id}" aria-label="Remove">
              <span class="ph-bold ph-x"></span>
            </button>
          </div>
          <div class="product-card-horizontal__bottom wishlist-drawer__line-actions">
            <div class="product-card-horizontal__price-row">${priceHtml}</div>
            <a href="${item.url}" class="wishlist-drawer__add-btn">View product</a>
          </div>
        </div>
      </article>
    `;
  }

  async function renderItems() {
    if (renderInProgress) {
      pendingRender = true;
      return;
    }

    if (!countEl) countEl = document.querySelector('[data-wishlist-drawer-count]');
    if (!emptyEl) emptyEl = document.querySelector('[data-wishlist-empty]');
    if (!itemsEl) itemsEl = document.querySelector('[data-wishlist-items]');
    if (!footerEl) footerEl = document.querySelector('[data-wishlist-footer]');

    if (!countEl || !emptyEl || !itemsEl || !footerEl) return;

    renderInProgress = true;

    try {
      countEl.textContent = `(${items.length} ${items.length === 1 ? 'item' : 'items'})`;
      updateWishlistCounts();

      if (items.length === 0) {
        emptyEl.hidden = false;
        itemsEl.hidden = true;
        footerEl.hidden = true;
        itemsEl.innerHTML = '';
        return;
      }

      emptyEl.hidden = true;
      itemsEl.hidden = false;
      footerEl.hidden = false;
      itemsEl.innerHTML = '<div class="wishlist-drawer__loading"><div class="wishlist-drawer__spinner"></div></div>';

      let html = '';
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.handle) {
          html += renderFallbackCard(item, i);
          continue;
        }

        const params = new URLSearchParams({ section_id: SECTION_ID, index: String(i) });
        if (item.variantId) params.set('variant', String(item.variantId));

        try {
          const response = await fetch(`/products/${encodeURIComponent(item.handle)}?${params}`);
          if (response.ok) {
            const cardHtml = parseSectionHtml(await response.text());
            if (cardHtml && cardHtml.length > 50) {
              html += cardHtml;
            } else {
              html += renderFallbackCard(item, i);
            }
          } else {
            html += renderFallbackCard(item, i);
          }
        } catch (error) {
          console.error('Failed to load wishlist line item:', error, item);
          html += renderFallbackCard(item, i);
        }
      }

      if (!html.trim()) {
        html = items.map((item, i) => renderFallbackCard(item, i)).join('');
      }
      itemsEl.innerHTML = html;

      if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
        window.UntamedIcons.refresh();
      }

      bindItemEvents();
    } finally {
      renderInProgress = false;
      if (pendingRender) {
        pendingRender = false;
        renderItems();
      }
    }
  }

  function updateWishlistCounts() {
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      if (items.length > 0) {
        el.textContent = items.length;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
    updateAllWishlistButtons();
  }

  function updateAllWishlistButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      const productId = parseInt(btn.dataset.productId, 10);
      btn.classList.toggle('is-active', hasItem(productId));
    });
  }

  function addItem(product) {
    if (hasItem(product.id)) return false;
    items.push({
      id: product.id,
      handle: product.handle,
      title: product.title,
      image: product.image,
      price: product.price,
      compareAtPrice: product.compareAtPrice || null,
      url: product.url,
      variantId: product.variantId || null,
    });
    saveToStorage();
    if (isOpen) renderItems();
    else updateWishlistCounts();
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
    return true;
  }

  function removeItem(productId) {
    const id = typeof productId === 'string' ? parseInt(productId, 10) : productId;
    const index = items.findIndex((item) => item.id == id);
    if (index === -1) return false;
    items.splice(index, 1);
    saveToStorage();
    if (isOpen) renderItems();
    else updateWishlistCounts();
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
    return true;
  }

  function toggleItem(product) {
    if (hasItem(product.id)) {
      return { added: false, removed: removeItem(product.id) };
    }
    return { added: addItem(product), removed: false };
  }

  function hasItem(productId) {
    return items.some((item) => item.id == productId);
  }

  function clearAll() {
    items = [];
    saveToStorage();
    renderItems();
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  function openDrawer() {
    if (isOpen) return;
    const menuDrawer = document.querySelector('[data-drawer]');
    menuWasOpen = menuDrawer && menuDrawer.getAttribute('aria-hidden') === 'false';
    isOpen = true;
    if (wrapper) wrapper.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wishlist-drawer-open');
    renderItems();
    setTimeout(() => closeBtn?.focus(), 100);
    document.dispatchEvent(new CustomEvent('wishlist:opened'));
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    if (wrapper) wrapper.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wishlist-drawer-open');
    if (menuWasOpen) {
      document.querySelector('[data-drawer-close]')?.focus();
      menuWasOpen = false;
    }
    document.dispatchEvent(new CustomEvent('wishlist:closed'));
  }

  function toggleDrawer() {
    if (isOpen) closeDrawer();
    else openDrawer();
  }

  function getVariantsData(lineEl) {
    const jsonEl = lineEl.querySelector('[data-wishlist-variants]');
    if (!jsonEl) return [];
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      return [];
    }
  }

  function updateLineForVariant(lineEl, variant) {
    if (!variant) return;

    const priceEl = lineEl.querySelector('[data-wishlist-price]');
    const imageEl = lineEl.querySelector('[data-wishlist-image]');
    const subtitleEl = lineEl.querySelector('[data-wishlist-subtitle]');
    const addBtn = lineEl.querySelector('[data-wishlist-add]');

    lineEl.dataset.variantId = variant.id;

    if (imageEl && variant.image) imageEl.src = variant.image;
    if (subtitleEl) subtitleEl.textContent = variant.title;

    if (priceEl) {
      if (variant.compare_at_price > variant.price) {
        priceEl.innerHTML = `<span class="product-card-horizontal__price product-card-horizontal__price--sale">${formatMoney(variant.price)}</span><span class="product-card-horizontal__compare-price">${formatMoney(variant.compare_at_price)}</span>`;
      } else {
        priceEl.innerHTML = `<span class="product-card-horizontal__price">${formatMoney(variant.price)}</span>`;
      }
    }

    if (addBtn) {
      if (!addBtn.dataset.labelAdd) addBtn.dataset.labelAdd = addBtn.textContent.trim();
      addBtn.dataset.labelSoldOut = addBtn.dataset.labelSoldOut || 'Sold out';
      addBtn.dataset.variantId = variant.id;
      addBtn.disabled = !variant.available;
      const label = variant.available ? addBtn.dataset.labelAdd : addBtn.dataset.labelSoldOut;
      addBtn.textContent = label;
    }

    const stored = items.find((item) => item.id == lineEl.dataset.productId);
    if (stored) {
      stored.variantId = variant.id;
      saveToStorage();
    }
  }

  async function addVariantToCart(variantId, productId, addBtn) {
    if (!variantId) return false;

    const originalText = addBtn?.textContent;
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = '…';
    }

    try {
      if (window.CartAPI && window.CartAPI.addItem) {
        await window.CartAPI.addItem(parseInt(variantId, 10), 1);
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
        });
        if (!response.ok) throw new Error('Failed to add to cart');
        window.dispatchEvent(new CustomEvent('cart:updated'));
      }

      if (addBtn) addBtn.textContent = '✓';

      if (productId) {
        setTimeout(() => removeItem(productId), 500);
      }
      return true;
    } catch (error) {
      console.error('Add to cart error:', error);
      if (addBtn) {
        addBtn.textContent = originalText || 'Add';
        addBtn.disabled = false;
      }
      return false;
    }
  }

  let itemEventsBound = false;

  function bindItemEvents() {
    if (!itemsEl || itemEventsBound) return;
    itemEventsBound = true;

    itemsEl.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        removeItem(removeBtn.dataset.productId);
        return;
      }

      const addBtn = e.target.closest('[data-wishlist-add]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        const lineEl = addBtn.closest('[data-wishlist-line-item]');
        const select = lineEl?.querySelector('[data-wishlist-variant-select]');
        const variantId = select ? select.value : addBtn.dataset.variantId;
        await addVariantToCart(variantId, lineEl?.dataset.productId, addBtn);
      }
    });

    itemsEl.addEventListener('change', (e) => {
      const select = e.target.closest('[data-wishlist-variant-select]');
      if (!select) return;
      const lineEl = select.closest('[data-wishlist-line-item]');
      if (!lineEl) return;
      const variants = getVariantsData(lineEl);
      const variant = variants.find((v) => String(v.id) === String(select.value));
      updateLineForVariant(lineEl, variant);
    });
  }

  function init() {
    getConfig();
    wrapper = document.querySelector('[data-wishlist-drawer]');
    if (!wrapper) return;

    overlay = wrapper.querySelector('[data-wishlist-overlay]');
    closeBtn = wrapper.querySelector('[data-wishlist-close]');
    countEl = wrapper.querySelector('[data-wishlist-drawer-count]');
    emptyEl = wrapper.querySelector('[data-wishlist-empty]');
    itemsEl = wrapper.querySelector('[data-wishlist-items]');
    footerEl = wrapper.querySelector('[data-wishlist-footer]');
    clearBtn = wrapper.querySelector('[data-wishlist-clear]');
    addAllBtn = wrapper.querySelector('[data-wishlist-add-all]');

    loadFromStorage();
    updateWishlistCounts();

    closeBtn?.addEventListener('click', closeDrawer);
    overlay?.addEventListener('click', closeDrawer);

    clearBtn?.addEventListener('click', () => {
      if (confirm(config.clearConfirm || 'Are you sure you want to clear your wishlist?')) {
        clearAll();
      }
    });

    addAllBtn?.addEventListener('click', async () => {
      const lines = itemsEl ? [...itemsEl.querySelectorAll('[data-wishlist-line-item]')] : [];
      const cartItems = [];
      const productIdsToRemove = [];

      lines.forEach((lineEl) => {
        const select = lineEl.querySelector('[data-wishlist-variant-select]');
        const addBtn = lineEl.querySelector('[data-wishlist-add]');
        let variantId = select ? select.value : addBtn?.dataset.variantId;
        if (variantId && addBtn && !addBtn.disabled) {
          cartItems.push({ id: parseInt(variantId, 10), quantity: 1 });
          productIdsToRemove.push(lineEl.dataset.productId);
        }
      });

      if (cartItems.length === 0) {
        alert(config.addAllEmptyMessage || 'No items available to add to cart.');
        return;
      }

      const addAllTextEl = addAllBtn.querySelector('.btn__text') || addAllBtn;
      const originalText = addAllTextEl.textContent;
      addAllBtn.disabled = true;
      addAllTextEl.textContent = config.addAllLoadingLabel || 'Adding...';

      try {
        if (window.CartAPI && window.CartAPI.addItems) {
          await window.CartAPI.addItems(cartItems);
        } else {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: cartItems }),
          });
          if (!response.ok) throw new Error('Failed to add items');
          window.dispatchEvent(new CustomEvent('cart:updated'));
        }

        addAllTextEl.textContent = config.addAllSuccessLabel || 'Added!';

        setTimeout(() => {
          productIdsToRemove.forEach((id) => {
            const idx = items.findIndex((item) => item.id == id);
            if (idx !== -1) items.splice(idx, 1);
          });
          saveToStorage();
          renderItems();
          document.dispatchEvent(new CustomEvent('wishlist:updated'));
          addAllTextEl.textContent = originalText;
          addAllBtn.disabled = false;
        }, 500);
      } catch (error) {
        console.error('Add all to cart error:', error);
        addAllTextEl.textContent = originalText;
        addAllBtn.disabled = false;
        alert(config.addAllErrorMessage || 'Failed to add items to cart. Please try again.');
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) closeDrawer();
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-wishlist-drawer-trigger]')) {
        e.preventDefault();
        openDrawer();
      }
    });

    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest('[data-wishlist-btn]');
      if (!wishlistBtn) return;
      e.preventDefault();
      e.stopPropagation();

      const handle = wishlistBtn.dataset.productHandle;
      if (!handle) return;

      const productData = {
        id: parseInt(wishlistBtn.dataset.productId, 10),
        handle,
        title: wishlistBtn.dataset.productTitle,
        image: wishlistBtn.dataset.productImage,
        price: parseInt(wishlistBtn.dataset.productPrice, 10),
        compareAtPrice: wishlistBtn.dataset.productCompareAtPrice
          ? parseInt(wishlistBtn.dataset.productCompareAtPrice, 10)
          : null,
        url: wishlistBtn.dataset.productUrl,
        variantId: wishlistBtn.dataset.variantId || null,
      };

      const result = toggleItem(productData);
      wishlistBtn.classList.add('is-animating');
      setTimeout(() => wishlistBtn.classList.remove('is-animating'), 300);

      if (result.added) {
        wishlistBtn.classList.add('is-active');
        openDrawer();
      } else {
        wishlistBtn.classList.remove('is-active');
      }
    });

    window.addEventListener('wishlist:open', openDrawer);
    window.addEventListener('wishlist:close', closeDrawer);
    window.addEventListener('wishlist:toggle', toggleDrawer);

    if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
      window.UntamedIcons.refresh();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Wishlist = {
    get items() {
      return [...items];
    },
    add: addItem,
    remove: removeItem,
    toggle: toggleItem,
    has: hasItem,
    clear: clearAll,
    open: openDrawer,
    close: closeDrawer,
    refresh: () => {
      loadFromStorage();
      renderItems();
    },
  };
})();
